import { useMemo } from 'react';
import { ClassSchedule } from '@/types/classTypes';
import { ScheduleConflictDetector, ScheduleConflict } from '@/utils/scheduleConflicts';

/**
 * Hook for managing schedule conflicts
 */
export const useScheduleConflicts = (schedules: ClassSchedule[]) => {
  // Detect all conflicts
  const conflicts = useMemo(() => {
    return ScheduleConflictDetector.detectConflicts(schedules);
  }, [schedules]);

  // Get conflict summary
  const conflictSummary = useMemo(() => {
    return ScheduleConflictDetector.getConflictSummary(conflicts);
  }, [conflicts]);

  // Helper to get conflicts for a specific schedule
  const getConflictsForSchedule = (scheduleId: string): ScheduleConflict[] => {
    return ScheduleConflictDetector.getConflictsForSchedule(scheduleId, conflicts);
  };

  // Helper to check if a schedule has conflicts
  const hasConflicts = (scheduleId: string): boolean => {
    return getConflictsForSchedule(scheduleId).length > 0;
  };

  // Helper to get the severity of conflicts for a schedule
  const getScheduleConflictSeverity = (scheduleId: string): 'none' | 'warning' | 'error' => {
    const scheduleConflicts = getConflictsForSchedule(scheduleId);
    if (scheduleConflicts.length === 0) return 'none';
    
    const hasError = scheduleConflicts.some(c => c.severity === 'error');
    return hasError ? 'error' : 'warning';
  };

  // Helper to validate a new schedule
  const validateNewSchedule = (newSchedule: Partial<ClassSchedule>): ScheduleConflict[] => {
    return ScheduleConflictDetector.validateNewSchedule(newSchedule, schedules);
  };

  // Helper to get conflict message for a schedule
  const getConflictMessage = (scheduleId: string): string => {
    const scheduleConflicts = getConflictsForSchedule(scheduleId);
    if (scheduleConflicts.length === 0) return '';
    
    const errorConflicts = scheduleConflicts.filter(c => c.severity === 'error');
    const warningConflicts = scheduleConflicts.filter(c => c.severity === 'warning');
    
    if (errorConflicts.length > 0) {
      return errorConflicts[0].message;
    }
    
    if (warningConflicts.length > 0) {
      return warningConflicts[0].message;
    }
    
    return scheduleConflicts[0].message;
  };

  return {
    conflicts,
    conflictSummary,
    getConflictsForSchedule,
    hasConflicts,
    getScheduleConflictSeverity,
    validateNewSchedule,
    getConflictMessage
  };
};